import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import { Breadcrumb, CustomSelect } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION } from "@constants/openSource";
import { TOAST } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CHARACTER_LENGTH } from "@constants/index";

export default function LicenseAdd() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [licenseData, setLicenseData] = useState({
    licenseId: "",
    licenseCategory: "PERMISSIVE",
    spdxStandardFlag: "N",
    osiApprovalFlag: "N",
    includingInReportFlag: "N",
    fossIncludingFlag: "N",
    copyleftFlag: "N",
    deprecationFlag: "N",
    updateFlag: "N",
    licenseNote: "",
    licenseText: "",
    licenseHeader: "",
    licenseComment: "",
    homepageUrl: "",
    licenseIdentifier: "",
    licenseName: "",
  });

  const [hasErrors, setHasErrors] = useState(true);
  const [isUnchanged, setIsUnchanged] = useState(true);

  const originalData = {
    licenseId: "",
    licenseCategory: "PERMISSIVE",
    spdxStandardFlag: "N",
    osiApprovalFlag: "N",
    includingInReportFlag: "N",
    fossIncludingFlag: "N",
    copyleftFlag: "N",
    deprecationFlag: "N",
    updateFlag: "N",
    licenseNote: "",
    licenseText: "",
    licenseHeader: "",
    licenseComment: "",
    homepageUrl: "",
    licenseIdentifier: "",
    licenseName: "",
  };

  // Validation
  useEffect(() => {
    const hasRequiredFields = licenseData.licenseIdentifier?.trim() && licenseData.licenseName?.trim();

    const dataChanged = JSON.stringify(licenseData) !== JSON.stringify(originalData);

    setHasErrors(!hasRequiredFields);
    setIsUnchanged(!dataChanged);
  }, [licenseData]);

  const handleInputChange = (field, value) => {
    setLicenseData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleChange = (field, value) => {
    setLicenseData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    navigate("/licenses");
  };

  // API call for adding license
  const addLicense = useMutationData(
    queryKeys.addLicense,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        showSuccessToast(t("open-source.license-add-success"));
        // Navigate to the detail page of the newly created license
        if (response && response.licenseSeq) {
          navigate(`/licenses/${response.licenseSeq}`);
        } else {
          navigate("/licenses");
        }
      },
      onError: (error) => {
        console.error("Error during create license:", error);
        const errorMessage = error?.response?.data || error?.message || "";
        if (errorMessage.includes("License already exists with the same license identifier.")) {
          ToastConstant(TOAST.error, t("open-source.license-already-exists"), "");
        } else {
          ToastConstant(TOAST.error, t("open-source.license-add-error"), "");
        }
      },
    },
  );

  const handleSave = async () => {
    if (!hasErrors && !isUnchanged) {
      // Trim all string values before sending
      const trimmedData = Object.keys(licenseData).reduce((acc, key) => {
        acc[key] = typeof licenseData[key] === "string" ? licenseData[key].trim() : licenseData[key];
        return acc;
      }, {});

      await addLicense.mutateAsync(trimmedData);
    }
  };

  return (
    <div className={`h-full ${colors.bgSecondary}`} data-testid="license-add-page">
      {/* Header with Breadcrumbs */}
      <div className="pb-3">
        <div className="space-y-2">
          <Breadcrumb
            items={[
              { label: t("open-source.license-list-title"), href: "/licenses" },
              { label: t("open-source.add-license-button") },
            ]}
          />
          <div className="flex items-center justify-between">
            <h1 className={`text-2xl font-bold ${colors.textPrimary}`}>{uppercase(t("open-source.add-new-license"))}</h1>
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid="license-add-cancel-button" onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid="license-add-save-button" onClick={handleSave} disabled={hasErrors || isUnchanged || addLicense.isPending}>
                {addLicense.isPending ? t("common.saving") : t("common.save-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form Section */}
      <div data-testid="license-add-form" className={`rounded-lg border ${colors.borderPrimary} ${colors.bgPrimary} p-6`}>
        <div className="space-y-6 mx-auto max-w-[60rem]">
          {/* License Information Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6`}>
              {uppercase(t("open-source.license-information-title"))}
            </h3>
            <div className="grid gap-4">
              {/* License Name */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-name")} <span className={colors.textRequired}>*</span>
                </label>
                <CustomInput
                  data-testid="license-add-name-input"
                  value={licenseData.licenseName || ""}
                  onChange={(e) => handleInputChange("licenseName", e.target.value)}
                  placeholder={t("open-source.license-name-placeholder")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>

              {/* License Identifier */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-identifier")} <span className={colors.textRequired}>*</span>
                </label>
                <CustomInput
                  data-testid="license-add-identifier-input"
                  value={licenseData.licenseIdentifier || ""}
                  onChange={(e) => handleInputChange("licenseIdentifier", e.target.value)}
                  placeholder={t("open-source.license-identifier-placeholder")}
                  size="md"
                  maxLength={CHARACTER_LENGTH.COMMENT}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4">
              {/* License Category */}
              <div className="col-span-1">
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-category")}
                </label>
                <div className="relative z-10">
                  <CustomSelect
                    options={LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.map((opt) => ({
                      value: opt.value,
                      label: t(opt.label),
                    }))}
                    value={
                      licenseData.licenseCategory
                        ? {
                          value: licenseData.licenseCategory,
                          label: t(
                            LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.find((o) => o.value === licenseData.licenseCategory)?.label ||
                            "",
                          ),
                        }
                        : null
                    }
                    onChange={(opt) => handleInputChange("licenseCategory", opt?.value || "")}
                    placeholder={t("open-source.license-category-placeholder")}
                    size="md"
                    menuPlacement="auto"
                  />
                </div>
              </div>

              {/* Homepage URL */}
              <div className="col-span-2">
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.homepage-url")}
                </label>
                <CustomInput
                  value={licenseData.homepageUrl || ""}
                  onChange={(e) => handleInputChange("homepageUrl", e.target.value)}
                  placeholder={t("open-source.homepage-url-placeholder")}
                  size="md"
                  maxLength={2048}
                />
              </div>
            </div>
          </div>

          {/* Technical Attributes Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6 pt-2`}>
              {uppercase(t("open-source.technical-attributes"))}
            </h3>
            <div className="grid grid-cols-4 gap-y-6 gap-x-2 justify-start">
              {/* First Row */}
              <div className="space-y-2">
                <label className={`block text-sm font-medium truncate ${colors.textSecondary}`}>
                  {t("open-source.spdx-standard")}
                </label>
                <div className="flex items-center">
                  <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={licenseData.spdxStandardFlag === "Y"}
                      onChange={(e) => handleToggleChange("spdxStandardFlag", e.target.checked ? "Y" : "N")}
                    />
                    <div
                      className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${licenseData.spdxStandardFlag === "Y" ? "border-none" : colors.borderPrimary} ${licenseData.spdxStandardFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                    ></div>
                    <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                      {licenseData.spdxStandardFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className={`block text-sm font-medium truncate ${colors.textSecondary}`}>
                  {t("open-source.osi-approval")}
                </label>
                <div className="flex items-center">
                  <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={licenseData.osiApprovalFlag === "Y"}
                      onChange={(e) => handleToggleChange("osiApprovalFlag", e.target.checked ? "Y" : "N")}
                    />
                    <div
                      className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${licenseData.osiApprovalFlag === "Y" ? "border-none" : colors.borderPrimary} ${licenseData.osiApprovalFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                    ></div>
                    <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                      {licenseData.osiApprovalFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  </label>
                </div>
              </div>


              <div className="space-y-2">
                <label className={`block text-sm font-medium truncate ${colors.textSecondary}`}>
                  {t("open-source.including-in-report")}
                </label>
                <div className="flex items-center">
                  <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={licenseData.includingInReportFlag === "Y"}
                      onChange={(e) => handleToggleChange("includingInReportFlag", e.target.checked ? "Y" : "N")}
                    />
                    <div
                      className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${licenseData.includingInReportFlag === "Y" ? "border-none" : colors.borderPrimary} ${licenseData.includingInReportFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                    ></div>
                    <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                      {licenseData.includingInReportFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  </label>
                </div>
              </div>

              {/* Second Row */}
              <div className="space-y-2">
                <label className={`block text-sm font-medium truncate ${colors.textSecondary}`}>
                  {t("open-source.copyleft")}
                </label>
                <div className="flex items-center">
                  <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={licenseData.copyleftFlag === "Y"}
                      onChange={(e) => handleToggleChange("copyleftFlag", e.target.checked ? "Y" : "N")}
                    />
                    <div
                      className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${licenseData.copyleftFlag === "Y" ? "border-none" : colors.borderPrimary} ${licenseData.copyleftFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                    ></div>
                    <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                      {licenseData.copyleftFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className={`block text-sm font-medium truncate ${colors.textSecondary}`}>
                  {t("open-source.deprecation")}
                </label>
                <div className="flex items-center">
                  <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={licenseData.deprecationFlag === "Y"}
                      onChange={(e) => handleToggleChange("deprecationFlag", e.target.checked ? "Y" : "N")}
                    />
                    <div
                      className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${licenseData.deprecationFlag === "Y" ? "border-none" : colors.borderPrimary} ${licenseData.deprecationFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                    ></div>
                    <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                      {licenseData.deprecationFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className={`block text-sm font-medium truncate ${colors.textSecondary}`}>
                  {t("open-source.update")}
                </label>
                <div className="flex items-center">
                  <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={licenseData.updateFlag === "Y"}
                      onChange={(e) => handleToggleChange("updateFlag", e.target.checked ? "Y" : "N")}
                    />
                    <div
                      className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${licenseData.updateFlag === "Y" ? "border-none" : colors.borderPrimary} ${licenseData.updateFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                    ></div>
                    <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                      {licenseData.updateFlag === "Y" ? t("common.yes") : t("common.no")}
                    </span>
                  </label>
                </div>
              </div>

              <div>{/* Empty space for alignment */}</div>
            </div>
          </div>

          {/* License Content Details Group */}
          <div>
            <h3 className={`text-md font-semibold ${colors.textPrimary} pb-6 pt-2`}>
              {uppercase(t("open-source.license-content-details"))}
            </h3>
            <div className="space-y-4">
              {/* License Note */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-note")}
                </label>
                <CustomTextarea
                  value={licenseData.licenseNote || ""}
                  onChange={(e) => handleInputChange("licenseNote", e.target.value)}
                  rows={3}
                  placeholder={t("open-source.license-note-placeholder")}
                  maxLength={CHARACTER_LENGTH.LICENSE_NOTE}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="license-add-license-note-char-count">
                  {(licenseData.licenseNote || "").length}/{CHARACTER_LENGTH.LICENSE_NOTE}
                </p>
              </div>

              {/* License Text */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-text")}
                </label>
                <CustomTextarea
                  value={licenseData.licenseText || ""}
                  onChange={(e) => handleInputChange("licenseText", e.target.value)}
                  rows={6}
                  placeholder={t("open-source.license-text-placeholder")}
                  maxLength={CHARACTER_LENGTH.LICENSE_TEXT}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="license-add-license-text-char-count">
                  {(licenseData.licenseText || "").length}/{CHARACTER_LENGTH.LICENSE_TEXT}
                </p>
              </div>

              {/* License Header */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-header")}
                </label>
                <CustomTextarea
                  value={licenseData.licenseHeader || ""}
                  onChange={(e) => handleInputChange("licenseHeader", e.target.value)}
                  rows={3}
                  placeholder={t("open-source.license-header-placeholder")}
                  maxLength={CHARACTER_LENGTH.LICENSE_HEADER}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="license-add-license-header-char-count">
                  {(licenseData.licenseHeader || "").length}/{CHARACTER_LENGTH.LICENSE_HEADER}
                </p>
              </div>

              {/* License Comment */}
              <div>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
                  {t("open-source.license-comment")}
                </label>
                <CustomTextarea
                  value={licenseData.licenseComment || ""}
                  onChange={(e) => handleInputChange("licenseComment", e.target.value)}
                  rows={3}
                  placeholder={t("open-source.license-comment-placeholder")}
                  maxLength={CHARACTER_LENGTH.LICENSE_COMMENT}
                  className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="license-add-license-comment-char-count">
                  {(licenseData.licenseComment || "").length}/{CHARACTER_LENGTH.LICENSE_COMMENT}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
